import { newRelic } from '~/utils/newRelic/newRelic';

import { SCAN_ACTIONS } from '../constants/scanActions.const';
import { createScanActionFailureScanAction } from '../scanActions/scanActionFailure.action';
import {
  type CreateScanActionHandlerOptions,
  type ScanAction,
  type ScanActionHandler,
  type ScanActionResult,
} from '../types/scanAction.type';
import { type ScanEvent } from '../types/scanEvent.type';
import { type ScanEventTracker } from '../types/scanEventTelemetry.type';
import {
  createScanActionExecutionError,
  createScanActionResolutionError,
} from './scanActionError.util';

/** The scan context worth reporting on. Never carries the scanned value itself. */
const scanAttributes = ({ correlationId, scanContext }: ScanEvent) => ({
  correlationId,
  hasBarcodeContent: Boolean(scanContext.barcodeContent),
  openDrawerName: scanContext.ui.openDrawerName,
  route: scanContext.route.route,
});

const NOOP_SCAN_EVENT_TRACKER: ScanEventTracker = {
  trackCompleted: () => {},
  trackFailed: () => {},
};

export const createScanActionHandler = ({
  failureAction = createScanActionFailureScanAction(),
  scanActions = SCAN_ACTIONS,
  tracker = NOOP_SCAN_EVENT_TRACKER,
}: CreateScanActionHandlerOptions = {}): ScanActionHandler => {
  const executeFailureScanAction = (
    description: string,
    error: Error,
    scanEvent: ScanEvent,
    actionName?: string,
  ): ScanActionResult => {
    newRelic.noticeError(description, {
      ...scanAttributes(scanEvent),
      ...(actionName && { actionName }),
      error,
    });
    tracker.trackFailed(scanEvent, error, actionName);

    try {
      return failureAction.execute(scanEvent, error);
    } catch (cause) {
      const failureError = createScanActionExecutionError(
        failureAction.name,
        scanEvent.correlationId,
        cause,
      );
      newRelic.noticeError('Failure: Failed to execute failure action', {
        ...scanAttributes(scanEvent),
        error: failureError,
      });

      return { error: failureError, success: false };
    }
  };

  const executeScanAction = (scanAction: ScanAction, scanEvent: ScanEvent): ScanActionResult => {
    newRelic.log('Scan action resolved', {
      customAttributes: { actionName: scanAction.name, correlationId: scanEvent.correlationId },
      level: 'DEBUG',
    });
    tracker.trackDispatched?.(scanEvent, scanAction.name);

    try {
      const result = scanAction.execute(scanEvent);

      if (result.success) tracker.trackCompleted(scanEvent, scanAction.name);
      else tracker.trackFailed(scanEvent, result.error, scanAction.name);

      return result;
    } catch (cause) {
      return executeFailureScanAction(
        'Failure: Failed to execute a scan action',
        createScanActionExecutionError(scanAction.name, scanEvent.correlationId, cause),
        scanEvent,
        scanAction.name,
      );
    }
  };

  return {
    handle: (scanEvent) => {
      try {
        const scanAction = scanActions.find((action) => action.matches(scanEvent));

        if (!scanAction) {
          return executeFailureScanAction(
            'Failure: No scan action found',
            createScanActionResolutionError(scanEvent.correlationId),
            scanEvent,
          );
        }

        return executeScanAction(scanAction, scanEvent);
      } catch (cause) {
        return executeFailureScanAction(
          'Failure: Failed to execute scan action',
          createScanActionResolutionError(scanEvent.correlationId, cause),
          scanEvent,
        );
      }
    },
  };
};
